'use client';

import type { EnglishIqResponseV2Type } from '@repo/sdk';
import { useCallback, useRef } from 'react';
import type { EnglishIqMessagesType } from '../english-iq-messages-type';
import { EnglishIqV2Question } from './assessment';
import { EnglishIqV2Demo } from './demo';
import { EnglishIqV2Result } from './result';
import { EnglishIqV2Start } from './start';
import type { IEnglishIqV2WebState } from './state';

interface IEnglishIqV2WizardStageProps {
  answer: (
    response: EnglishIqResponseV2Type,
    replayCount: number,
    responseTimeMs: number,
  ) => Promise<void>;
  finishDemo: () => void;
  friendResult: { score: number; subBand: string } | null;
  locale: string;
  messages: EnglishIqMessagesType;
  isStartPending: boolean;
  onStart: () => void;
  reset: () => void;
  retryPendingRequest: () => Promise<void>;
  state: IEnglishIqV2WebState;
}

export function EnglishIqV2WizardStage({
  answer,
  finishDemo,
  friendResult,
  locale,
  messages,
  isStartPending,
  onStart,
  reset,
  retryPendingRequest,
  state,
}: IEnglishIqV2WizardStageProps): React.JSX.Element {
  const firstDemoClipRef = useRef<HTMLVideoElement>(null);
  const isDemoActive = state.view === 'demo';
  const shouldMountDemo =
    isDemoActive || (state.demo !== null && state.assessment.answeredCount >= 10);
  const submitAnswer = useCallback(
    (response: EnglishIqResponseV2Type, replayCount: number, responseTimeMs: number) => {
      void answer(response, replayCount, responseTimeMs);
    },
    [answer],
  );
  const playDemoFromFinalAnswer = useCallback(() => {
    const playback = firstDemoClipRef.current?.play();
    void playback?.catch(() => undefined);
  }, []);
  return (
    <>
      <ActiveStage
        friendResult={friendResult}
        isStartPending={isStartPending}
        locale={locale}
        messages={messages}
        onStart={onStart}
        playDemoFromFinalAnswer={playDemoFromFinalAnswer}
        reset={reset}
        state={state}
        submitAnswer={submitAnswer}
      />
      {shouldMountDemo ? (
        <div aria-hidden={!isDemoActive} className={isDemoActive ? undefined : 'hidden'}>
          <EnglishIqV2Demo
            active={isDemoActive}
            canFinish={state.pendingResult !== null}
            demo={state.demo}
            firstClipRef={firstDemoClipRef}
            messages={messages.v2.demo}
            onFinish={finishDemo}
          />
        </div>
      ) : null}
      <RequestError
        messages={messages.v2.assessment}
        onReset={reset}
        onRetry={retryPendingRequest}
        state={state}
      />
    </>
  );
}

interface IActiveStageProps {
  friendResult: { score: number; subBand: string } | null;
  locale: string;
  messages: EnglishIqMessagesType;
  isStartPending: boolean;
  onStart: () => void;
  reset: () => void;
  state: IEnglishIqV2WebState;
  playDemoFromFinalAnswer: () => void;
  submitAnswer: (
    response: EnglishIqResponseV2Type,
    replayCount: number,
    responseTimeMs: number,
  ) => void;
}

function ActiveStage({
  friendResult,
  locale,
  messages,
  isStartPending,
  onStart,
  playDemoFromFinalAnswer,
  reset,
  state,
  submitAnswer,
}: IActiveStageProps): React.JSX.Element | null {
  if (!state.hydrated) {
    return (
      <div aria-live="polite" className="grid min-h-[100svh] place-items-center" role="status">
        {messages.v2.assessment.preparing}
      </div>
    );
  }
  if (state.view === 'start') {
    return (
      <EnglishIqV2Start
        friendResult={friendResult}
        isBusy={isStartPending}
        messages={messages.v2.start}
        onStart={onStart}
      />
    );
  }
  if (state.view === 'assessment' && state.assessment.currentItem) {
    return (
      <div className="mx-auto flex min-h-[100svh] max-w-6xl items-center px-4 py-[max(1rem,env(safe-area-inset-top))] sm:px-8">
        <EnglishIqV2Question
          answered={state.assessment.answeredCount}
          isBusy={state.assessment.phase === 'submitting' || state.pendingAnswer !== null}
          item={state.assessment.currentItem}
          key={state.assessment.currentItem.id}
          messages={messages.v2.assessment}
          onSubmit={submitAnswer}
          onFinalAnswer={playDemoFromFinalAnswer}
          total={state.assessment.totalCount}
        />
      </div>
    );
  }
  if (state.view !== 'demo' && state.assessment.result) {
    return (
      <EnglishIqV2Result
        locale={locale}
        messages={messages.v2.result}
        onRetake={reset}
        owned
        result={state.assessment.result}
      />
    );
  }
  return null;
}

interface IRequestErrorProps {
  messages: EnglishIqMessagesType['v2']['assessment'];
  onReset: () => void;
  onRetry: () => Promise<void>;
  state: IEnglishIqV2WebState;
}

function RequestError({
  messages,
  onReset,
  onRetry,
  state,
}: IRequestErrorProps): React.JSX.Element | null {
  if (!state.error) {
    return null;
  }
  const hasPendingRequest = state.pendingAnswer !== null || state.pendingCompletionId !== null;
  return (
    <div
      className="fixed inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-20 mx-auto flex max-w-xl items-center justify-between gap-4 rounded-2xl border border-red-300/20 bg-red-950/90 p-4 shadow-2xl"
      role="alert"
    >
      <p className="text-sm">{messages.error}</p>
      <button
        className="shrink-0 font-bold text-[#ffb36b]"
        onClick={() => (hasPendingRequest ? void onRetry() : onReset())}
        type="button"
      >
        {hasPendingRequest ? messages.retry : messages.restart}
      </button>
    </div>
  );
}
